import {FormatListBulletedOutlined, KeyboardArrowRightOutlined, LocalOfferOutlined} from "@mui/icons-material";
import {Box, ButtonBase, CssBaseline, Stack, ThemeProvider, Typography} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import {FC, KeyboardEvent, MouseEvent, SyntheticEvent, useState} from "react";
import {useTranslation} from "react-i18next";
import CustomDialog from "../custom-dialog/CustomDialog";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";

interface Props {
    cost: number
    originalCost: number | null
    drawCount?: number
    drawNames?: string[]
    color: string
}

const BundleDealBadge: FC<Props> = ({cost, originalCost, drawCount = 0, drawNames = [], color}) => {
    const theme = useTheme()
    const {t} = useTranslation()
    const [includedDrawsOpen, setIncludedDrawsOpen] = useState(false)
    const hasDiscount = !!originalCost && originalCost > cost
    const discountPercent = hasDiscount ? Math.floor(((originalCost - cost) / originalCost) * 100) : 0
    const contrastText = theme.palette.tertiary.contrastText ?? theme.palette.primary.contrastText

    const stopCardNavigation = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault()
        event.stopPropagation()
    }

    const stopDialogPropagation = (event: SyntheticEvent) => {
        event.stopPropagation()
    }

    const handleOpenIncludedDraws = (event: MouseEvent<HTMLElement>) => {
        stopCardNavigation(event)
        setIncludedDrawsOpen(true)
    }

    const handleIncludedDrawsKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key !== "Enter" && event.key !== " ") {
            return
        }

        event.preventDefault()
        event.stopPropagation()
        setIncludedDrawsOpen(true)
    }

    const handleCloseIncludedDraws = () => {
        setIncludedDrawsOpen(false)
    }

    return (
        <Stack alignItems="center" spacing={0.55} mt={0.85} width="100%">
            <Box
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.45,
                    maxWidth: "100%",
                    px: 0.9,
                    py: 0.4,
                    borderRadius: "999px",
                    color: contrastText,
                    backgroundColor: theme.palette.tertiary.main,
                    border: `1px solid ${alpha(contrastText, 0.3)}`,
                    boxShadow: `0 2px 8px ${alpha("#000", 0.22)}`,
                }}
            >
                <LocalOfferOutlined sx={{fontSize: 15, flexShrink: 0}}/>
                <Typography
                    variant="caption"
                    fontWeight={800}
                    noWrap
                    sx={{lineHeight: 1.1}}
                >
                    {hasDiscount
                        ? `${t("bundle_deal")} · ${t("save_percent", {percent: discountPercent})}`
                        : t("bundle_deal")}
                </Typography>
            </Box>
            {drawCount > 0 && (
                <>
                    <ButtonBase
                        component="span"
                        role="button"
                        tabIndex={0}
                        onClick={handleOpenIncludedDraws}
                        onKeyDown={handleIncludedDrawsKeyDown}
                        aria-label={t("bundle_view_included_draws")}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.3,
                            maxWidth: "100%",
                            borderRadius: "999px",
                            px: 0.45,
                            py: 0.2,
                            color: alpha(color, 0.86),
                            cursor: "pointer",
                            transition: "background-color 0.15s ease",
                            "&:hover": {
                                backgroundColor: alpha(color, 0.07),
                            },
                        }}
                    >
                        <FormatListBulletedOutlined sx={{fontSize: 15, color: alpha(color, 0.84), flexShrink: 0}}/>
                        <Typography
                            component="span"
                            variant="caption"
                            fontWeight={700}
                            noWrap
                            sx={{
                                color: alpha(color, 0.86),
                                lineHeight: 1.1,
                                borderBottom: `1px dotted ${alpha(color, 0.55)}`,
                                minWidth: 0,
                            }}
                        >
                            {t("bundle_included_draws_count", {count: drawCount})}
                        </Typography>
                        <KeyboardArrowRightOutlined sx={{fontSize: 17, color: alpha(color, 0.78), flexShrink: 0}}/>
                    </ButtonBase>

                    {drawNames.length > 0 && (
                        <Box
                            onClick={stopDialogPropagation}
                            onMouseDown={stopDialogPropagation}
                            onTouchStart={stopDialogPropagation}
                        >
                            <ThemeProvider theme={whiteInputsTheme}>
                                <CssBaseline/>
                                <CustomDialog
                                    open={includedDrawsOpen}
                                    handleDialogClose={handleCloseIncludedDraws}
                                    title={t("included_draws")}
                                    maxWidth="xs"
                                    dialogId="bundle-included-draws-dialog"
                                    content={(
                                        <Stack spacing={1.5}>
                                            <Typography
                                                variant="body2"
                                                sx={{color: alpha(theme.palette.common.white, 0.78)}}
                                            >
                                                {t("bundle_tooltip_explainer")}
                                            </Typography>

                                            <Stack spacing={1}>
                                                {drawNames.map((name, index) => (
                                                    <Box
                                                        key={`${name}-${index}`}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={700}
                                                            sx={{color, minWidth: 22}}
                                                        >
                                                            {index + 1}.
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{color: theme.palette.common.white, lineHeight: 1.4}}
                                                        >
                                                            {name}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Stack>
                                    )}
                                />
                            </ThemeProvider>
                        </Box>
                    )}
                </>
            )}
        </Stack>
    )
}

export default BundleDealBadge;
